import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, Users, Ticket, SignOut, ChartBar, Plus, SpeakerHigh, SpeakerSlash, Eye, CheckCircle, XCircle, Bank, CreditCard, ShieldCheck, WarningCircle, EnvelopeSimple, Download, Database } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/App';
import { useLanguage } from '@/i18n/LanguageContext';
import { getT } from '@/i18n/translations';
import PasswordInput from '@/components/PasswordInput';
import UserDetailModal from '@/components/UserDetailModal';
import BrandLogo from '@/components/BrandLogo';
import Admin2FAPanel from '@/components/Admin2FAPanel';
import AdminAnalyticsPanel from '@/components/AdminAnalyticsPanel';
import TestAccountsCleanupPanel from '@/components/TestAccountsCleanupPanel';
import EmailDiagnosticPanel from '@/components/EmailDiagnosticPanel';
import AdminAffiliatePanel from '@/components/AdminAffiliatePanel';

const AdminDashboard = ({ user, token, onLogout, isMuted, toggleMute }) => {
  const { language } = useLanguage();
  const t = getT('admin', language);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [draws, setDraws] = useState([]);
  const [kycCases, setKycCases] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [payments, setPayments] = useState([]);
  const [riskFlags, setRiskFlags] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [autopilot, setAutopilot] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [showCreateDraw, setShowCreateDraw] = useState(false);
  const [drawNumber, setDrawNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [detailUserId, setDetailUserId] = useState(null);

  // Users search/filter state — synced with URL query params
  const [searchParams, setSearchParams] = useSearchParams();
  const [usersData, setUsersData] = useState({ items: [], total: 0, page: 1, pageSize: 25, totalPages: 0 });
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const filters = {
    q: searchParams.get('q') || '',
    accountStatus: searchParams.get('accountStatus') || '',
    verificationStatus: searchParams.get('verificationStatus') || '',
    role: searchParams.get('role') || '',
    country: searchParams.get('country') || '',
    registeredWithinDays: searchParams.get('registeredWithinDays') || '',
    page: parseInt(searchParams.get('page') || '1', 10),
    pageSize: parseInt(searchParams.get('pageSize') || '25', 10),
    sortBy: searchParams.get('sortBy') || 'createdAt',
    sortDir: searchParams.get('sortDir') || 'desc',
  };
  const debounceRef = useRef(null);

  const updateFilter = useCallback((key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === '' || value == null) next.delete(key);
    else next.set(key, String(value));
    // Reset page to 1 when any non-page filter changes
    if (key !== 'page' && key !== 'pageSize') next.set('page', '1');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v != null) params.set(k, String(v));
      });
      const r = await fetch(`${API}/admin/users?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setUsersData(d);
        setUsers(d.items);
      }
    } catch (e) {
      console.error('fetchUsers failed', e);
    } finally {
      setUsersLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, searchParams]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Debounce search input → URL
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (searchInput !== filters.q) updateFilter('q', searchInput);
    }, 350);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const clearFilters = () => {
    setSearchInput('');
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, readinessRes, autopilotRes, reconciliationRes, usersRes, drawsRes, kycRes, withdrawalsRes, paymentsRes, riskRes, auditRes, backupRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/readiness`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/autopilot/status`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/reconciliation`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/users`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/draws`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/kyc/cases`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/withdrawals`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/payments`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/risk-flags`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/audit-logs`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/backups/status`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (readinessRes.ok) setReadiness(await readinessRes.json());
      if (autopilotRes.ok) setAutopilot(await autopilotRes.json());
      if (reconciliationRes.ok) setReconciliation(await reconciliationRes.json());
      // users handled by fetchUsers() (server-side paginated); skip naive consumption here
      if (drawsRes.ok) setDraws(await drawsRes.json());
      if (kycRes.ok) setKycCases(await kycRes.json());
      if (withdrawalsRes.ok) setWithdrawals(await withdrawalsRes.json());
      if (paymentsRes.ok) setPayments(await paymentsRes.json());
      if (riskRes.ok) setRiskFlags(await riskRes.json());
      if (auditRes.ok) setAuditLogs(await auditRes.json());
      if (backupRes.ok) setBackupStatus(await backupRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 3000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleCreateDraw = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/draws/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ drawNumber: parseInt(drawNumber) })
      });
      
      if (response.ok) {
        toast.success('Draw created successfully!');
        setShowCreateDraw(false);
        setDrawNumber('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create draw');
      }
    } catch (error) {
      toast.error('Failed to create draw');
    }
    setLoading(false);
  };

  const readJsonOnce = async (response) => {
    const text = await response.text();
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch {
      return {};
    }
  };

  const handleReviewKyc = async (userId, decision, reason = '') => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/kyc/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ userId, decision, reason })
      });
      if (response.ok) {
        toast.success(decision === 'approved' ? 'KYC approved' : 'KYC rejected');
        fetchData();
      } else {
        const error = await readJsonOnce(response);
        toast.error(error.detail || 'KYC review failed');
      }
    } catch {
      toast.error('KYC review failed');
    }
    setLoading(false);
  };

  const handleRejectKyc = (userId) => {
    const reason = window.prompt('Reason for rejection');
    if (reason !== null) {
      handleReviewKyc(userId, 'rejected', reason);
    }
  };

  const handleTransferAdmin = async (userId, email) => {
    if (!window.confirm(
      `⚠ TRANSFER ADMIN ROLE\n\n` +
      `From: ${user?.email} (you)\n` +
      `To:   ${email}\n\n` +
      `You will lose admin access immediately. The target user will become the sole admin.\n\n` +
      `Continue?`
    )) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/users/${userId}/transfer-admin`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(`Admin role transferred to ${d.toEmail}. You are now a regular user. Logging out…`);
        setTimeout(() => onLogout(), 2000);
      } else {
        toast.error(d.detail || `HTTP ${r.status}`);
      }
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };

  // handleResetUserPassword moved into UserDetailModal (admin opens user details → Reset password action).

  const handleWithdrawalReview = async (withdrawalId, action, reason = '') => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/withdrawals/${withdrawalId}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason })
      });
      if (response.ok) {
        toast.success(action === 'approve' ? 'Withdrawal marked as paid' : 'Withdrawal rejected and refunded');
        fetchData();
      } else {
        const error = await readJsonOnce(response);
        toast.error(error.detail || 'Withdrawal review failed');
      }
    } catch {
      toast.error('Withdrawal review failed');
    }
    setLoading(false);
  };

  const handleRejectWithdrawal = (withdrawalId) => {
    const reason = window.prompt('Reason for rejection');
    if (reason !== null) {
      handleWithdrawalReview(withdrawalId, 'reject', reason);
    }
  };

  const handleResolveRiskFlag = async (flagId) => {
    const note = window.prompt('Resolution note') || '';
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/risk-flags/${flagId}/resolve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ note })
      });
      if (response.ok) {
        toast.success('Risk flag resolved');
        fetchData();
      } else {
        const error = await readJsonOnce(response);
        toast.error(error.detail || 'Risk flag update failed');
      }
    } catch {
      toast.error('Risk flag update failed');
    }
    setLoading(false);
  };

  const handleSyncPayment = async (paymentId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/payments/${paymentId}/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await readJsonOnce(response);
      if (response.ok) {
        toast.success(`Payment status: ${data.paymentIntent?.status || 'synced'}`);
        fetchData();
      } else {
        toast.error(data.detail || 'Payment sync failed');
      }
    } catch {
      toast.error('Payment sync failed');
    }
    setLoading(false);
  };

  const handleSendTestEmail = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/email/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({})
      });
      const data = await readJsonOnce(response);
      if (response.ok) {
        toast.success(`SMTP test sent to ${data.recipient}`);
        fetchData();
      } else {
        toast.error(data.detail || 'SMTP test failed');
      }
    } catch {
      toast.error('SMTP test failed');
    }
    setLoading(false);
  };

  const handleDownloadBackup = async () => {
    if (backupLoading) return;
    setBackupLoading(true);
    try {
      const response = await fetch(`${API}/admin/backups/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.status === 429) {
        toast.error('Rate limit reached. Wait up to 1 hour before exporting again.');
        return;
      }
      if (!response.ok) {
        toast.error(`Backup export failed (HTTP ${response.status})`);
        return;
      }
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `1in2000club-backup-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.zip`;
      const totalDocs = response.headers.get('X-Backup-Total-Documents');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Backup downloaded (${totalDocs || '?'} documents). Store encrypted and offline.`);
      fetchData();
    } catch (e) {
      toast.error(`Backup failed: ${e.message}`);
    } finally {
      setBackupLoading(false);
    }
  };

  const formatRelativeTime = (iso) => {
    if (!iso) return 'never';
    try {
      const then = new Date(iso).getTime();
      const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
      if (diffSec < 60) return `${diffSec}s ago`;
      if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
      if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
      return `${Math.floor(diffSec / 86400)}d ago`;
    } catch { return 'unknown'; }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 12) { toast.error('New password must be at least 12 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setPasswordLoading(true);
    try {
      const response = await fetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ currentPassword, newPassword, confirmPassword })
      });
      const data = await readJsonOnce(response);
      if (response.ok) {
        toast.success('Password changed. Please log out and log in with your new password.');
        setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      } else {
        toast.error(data?.detail || 'Password change failed');
      }
    } catch (err) { toast.error('Network error'); }
    setPasswordLoading(false);
  };


  const handleRunAutopilot = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/autopilot/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ syncProviders: false, maxProviderSync: 25 })
      });
      const data = await readJsonOnce(response);
      if (response.ok) {
        const warningCount = data.summary?.warnings || 0;
        const errorCount = data.summary?.errors || 0;
        if (errorCount > 0) {
          toast.error(`Autopilot finished with ${errorCount} error${errorCount === 1 ? '' : 's'}`);
        } else if (warningCount > 0) {
          toast.warning(`Autopilot finished with ${warningCount} warning${warningCount === 1 ? '' : 's'}`);
        } else {
          toast.success('Autopilot completed');
        }
        fetchData();
      } else {
        toast.error(data.detail || 'Autopilot run failed');
      }
    } catch {
      toast.error('Autopilot run failed');
    }
    setLoading(false);
  };

  const openKycDocument = async (userId, documentType) => {
    try {
      const response = await fetch(`${API}/admin/kyc/document/${userId}/${documentType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const error = await readJsonOnce(response);
        toast.error(error.detail || 'Document could not be opened');
        return;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error('Document could not be opened');
    }
  };

  const documentLabels = {
    id_front: 'OP front',
    id_back: 'OP back',
    selfie: 'Selfie'
  };

  const readinessItems = readiness?.items || [];
  const readinessSummary = readiness?.summary || {};
  const autopilotQueues = autopilot?.queues || {};
  const autopilotLastRun = autopilot?.lastRun;
  const autopilotLastActions = autopilotLastRun?.actions || [];
  const reconciliationSummary = reconciliation?.summary || {};
  const reconciliationIssues = reconciliation?.issues || [];
  const reconciliationStatusClass = {
    ok: 'bg-green-500/10 text-green-300 border-green-500/20',
    needs_attention: 'bg-red-500/10 text-red-300 border-red-500/20'
  };
  const formatEur = (value) => `EUR ${Number(value || 0).toFixed(2)}`;
  const readinessStatusLabel = {
    blocked: 'Blocked',
    needs_attention: 'Needs attention',
    ready: 'Ready'
  };
  const readinessStatusClass = {
    blocked: 'bg-red-500/10 text-red-300 border-red-500/20',
    needs_attention: 'bg-[#cba135]/10 text-[#cba135] border-[#cba135]/20',
    ready: 'bg-green-500/10 text-green-300 border-green-500/20'
  };
  const readinessItemClass = (statusValue) => {
    if (statusValue === 'ok') return 'border-green-500/15 bg-green-500/5';
    if (statusValue === 'warning') return 'border-[#cba135]/20 bg-[#cba135]/5';
    return 'border-red-500/20 bg-red-500/5';
  };
  const readinessDotClass = (statusValue) => {
    if (statusValue === 'ok') return 'bg-green-400';
    if (statusValue === 'warning') return 'bg-[#cba135]';
    return 'bg-red-400';
  };
  const paymentReviewStatuses = ['created', 'pending', 'underpaid', 'overpaid', 'manual_review', 'provider_verification_failed', 'failed'];
  const paymentSuccessStatuses = ['credited', 'paid', 'confirmed', 'overpaid'];
  const paymentDangerStatuses = ['underpaid', 'failed', 'manual_review', 'provider_verification_failed'];
  const paymentSyncStatuses = ['created', 'pending', 'underpaid', 'manual_review', 'provider_verification_failed'];
  const paymentStatusClass = (statusValue) => {
    if (paymentSuccessStatuses.includes(statusValue)) return 'bg-green-500/10 text-green-300';
    if (paymentDangerStatuses.includes(statusValue)) return 'bg-red-500/10 text-red-300';
    return 'bg-[#cba135]/10 text-[#cba135]';
  };
  const canSyncPayment = (payment) => (
    ['coingate', 'payram'].includes(payment.provider) && paymentSyncStatuses.includes(payment.status)
  );

  return (
    <div className="min-h-screen bg-[#0e0b04]">
      <header className="bg-[#0e0b04]/70 backdrop-blur-xl border-b border-[#cba135]/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
              <BrandLogo />
              <h1 className="heading-font text-2xl font-light tracking-tight text-[#cba135]">{t.title}</h1>
          </div>
          <div className="flex items-center gap-4">
            <Button
              data-testid="admin-mute-button"
              onClick={toggleMute}
              variant="outline"
              size="icon"
              className="border-[#cba135]/20 text-[#cba135] hover:bg-[#cba135]/10 hover:text-[#cba135] bg-[#1a1508]"
            >
              {isMuted ? (
                <SpeakerSlash size={20} weight="fill" />
              ) : (
                <SpeakerHigh size={20} weight="fill" />
              )}
            </Button>
            <Button 
              data-testid="admin-logout-button"
              onClick={onLogout}
              variant="outline"
              className="border-[#cba135] text-[#cba135] hover:bg-[#cba135]/10"
            >
              <SignOut size={20} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h2 className="heading-font text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#cba135] mb-8">
            {t.overview}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-[#1a1508] border border-[#cba135]/10 shadow-[0_8px_32px_rgba(0,0,0,0.6)] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-2">
                <Users size={24} weight="duotone" className="text-[#cba135]" />
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">{t.totalUsers}</p>
              </div>
              <p data-testid="total-users" className="text-3xl font-bold text-[#cba135]">{stats?.totalUsers || 0}</p>
            </div>

            <div className="bg-[#1a1508] border border-[#cba135]/10 shadow-[0_8px_32px_rgba(0,0,0,0.6)] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-2">
                <Ticket size={24} weight="duotone" className="text-[#cba135]" />
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">{t.totalDraws}</p>
              </div>
              <p data-testid="total-draws" className="text-3xl font-bold text-[#cba135]">{stats?.totalDraws || 0}</p>
            </div>

            <div className="bg-[#1a1508] border border-[#cba135]/10 shadow-[0_8px_32px_rgba(0,0,0,0.6)] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-2">
                <Trophy size={24} weight="duotone" className="text-[#cba135]" />
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">{t.completedDraws}</p>
              </div>
              <p data-testid="completed-draws" className="text-3xl font-bold text-[#cba135]">{stats?.completedDraws || 0}</p>
            </div>

            <div className="bg-[#1a1508] border border-[#cba135]/10 shadow-[0_8px_32px_rgba(0,0,0,0.6)] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-2">
                <ChartBar size={24} weight="duotone" className="text-[#cba135]" />
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">{t.openRisk}</p>
              </div>
              <p data-testid="open-risk-flags" className="text-3xl font-bold text-[#cba135]">{stats?.openRiskFlags || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.03 }}
          className="mb-10"
          data-testid="admin-analytics-section"
        >
          <AdminAnalyticsPanel token={token} />
        </motion.div>

        <AdminAffiliatePanel token={token} apiBase={API} />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-10"
          data-testid="admin-security-section"
        >
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck size={26} weight="duotone" className="text-[#cba135]" />
            <h3 className="heading-font text-lg sm:text-xl font-medium text-[#cba135]">Security</h3>
          </div>
          <Admin2FAPanel />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.07 }}
          className="mb-10"
          data-testid="admin-backups-section"
        >
          <div className="flex items-center gap-3 mb-4">
            <Database size={26} weight="duotone" className="text-[#cba135]" />
            <h3 className="heading-font text-lg sm:text-xl font-medium text-[#cba135]">Backups</h3>
          </div>
          <div className="bg-[#1a1508] border border-[#cba135]/15 shadow-[0_8px_32px_rgba(0,0,0,0.6)] rounded-lg p-5 sm:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-5 items-start lg:items-center">
              <div className="space-y-2">
                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Last export</p>
                    <p data-testid="backup-last-export" className="text-sm text-[#cba135] mt-1">
                      {backupStatus?.lastExportAt
                        ? `${formatRelativeTime(backupStatus.lastExportAt)}  ·  ${new Date(backupStatus.lastExportAt).toISOString().replace('T', ' ').slice(0, 19)} UTC`
                        : 'never'}
                    </p>
                    {backupStatus?.lastExportBy && (
                      <p className="text-xs text-[#cba135]/60 mt-0.5">by {backupStatus.lastExportBy}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Exports (24h)</p>
                    <p data-testid="backup-exports-24h" className="text-sm text-[#cba135] mt-1">
                      {backupStatus?.exportsLast24h ?? 0}
                      <span className="text-[#cba135]/50 text-xs ml-2">
                        / {backupStatus?.rateLimit?.limit ?? 3} per {backupStatus?.rateLimit?.windowHours ?? 1}h
                      </span>
                    </p>
                  </div>
                  {backupStatus?.lastExportMetadata?.totalDocuments != null && (
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Last size</p>
                      <p className="text-sm text-[#cba135] mt-1">
                        {backupStatus.lastExportMetadata.totalDocuments.toLocaleString()} docs
                        {backupStatus.lastExportMetadata.sizeBytes && (
                          <span className="text-[#cba135]/50 text-xs ml-2">
                            · {(backupStatus.lastExportMetadata.sizeBytes / 1024).toFixed(1)} KB
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex items-start gap-2 mt-3 p-3 bg-red-900/15 border border-red-500/20 rounded">
                  <WarningCircle size={18} weight="fill" className="text-red-300 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-200 leading-relaxed">
                    <span className="font-bold">Store this file encrypted and offline.</span>{' '}
                    The archive contains sensitive operational data (user emails, password hashes,
                    KYC metadata, payment ledger). Never commit it to source control. See{' '}
                    <code className="text-red-100 bg-red-900/30 px-1 rounded">BACKUP_RESTORE_RUNBOOK.md</code>{' '}
                    for restore steps.
                  </p>
                </div>
                {backupStatus?.signing && (
                  <div
                    data-testid="backup-signing-status"
                    className={`flex items-start gap-2 mt-2 p-2.5 rounded text-xs leading-relaxed ${
                      backupStatus.signing.enabled
                        ? 'bg-green-900/15 border border-green-500/25 text-green-200'
                        : 'bg-[#cba135]/10 border border-[#cba135]/30 text-[#cba135]'
                    }`}
                  >
                    {backupStatus.signing.enabled ? (
                      <>
                        <CheckCircle size={16} weight="fill" className="text-green-400 flex-shrink-0 mt-0.5" />
                        <span>
                          <span className="font-bold">Signing enabled</span> · {backupStatus.signing.algorithm}.
                          Each backup contains <code className="bg-green-900/30 px-1 rounded">MANIFEST.sig</code> for offline integrity verification.
                        </span>
                      </>
                    ) : (
                      <>
                        <WarningCircle size={16} weight="fill" className="text-[#cba135] flex-shrink-0 mt-0.5" />
                        <span>
                          <span className="font-bold">Backup signing disabled.</span>{' '}
                          Set <code className="bg-[#cba135]/15 px-1 rounded">BACKUP_SIGNING_SECRET</code> in backend env to enable HMAC-SHA256 integrity checks.
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <Button
                data-testid="backup-download-button"
                onClick={handleDownloadBackup}
                disabled={backupLoading}
                className="bg-[#cba135] text-[#0e0b04] hover:bg-[#d4b347] font-semibold w-full lg:w-auto"
              >
                <Download size={18} weight="bold" className="mr-2" />
                {backupLoading ? 'Preparing…' : 'Download DB backup'}
              </Button>
            </div>
            <R2BackupStatus />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="mb-12"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <ShieldCheck size={30} weight="duotone" className="text-[#cba135]" />
              <h3 className="heading-font text-xl sm:text-2xl font-medium text-[#cba135]">{t.systemReadiness}</h3>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <Button
                data-testid="smtp-test-button"
                onClick={handleSendTestEmail}
                disabled={loading || !readiness?.environment?.smtpConfigured}
                variant="outline"
                className="border-[#cba135]/20 text-[#cba135] hover:bg-[#cba135]/10 hover:text-[#cba135]"
                title={!readiness?.environment?.smtpConfigured ? 'SMTP not configured' : 'Send test email'}
              >
                <EnvelopeSimple size={18} className="mr-2" />
                {t.testEmail}
              </Button>
              <EmailDiagnosticPanel
                token={token}
                smtpConfigured={readiness?.environment?.smtpConfigured}
                defaultEmail={user?.email || ''}
              />
              <span className={`inline-flex w-fit items-center rounded-md border px-3 py-2 text-xs uppercase tracking-[0.15em] font-bold ${readinessStatusClass[readiness?.overallStatus] || readinessStatusClass.needs_attention}`}>
                {readinessStatusLabel[readiness?.overallStatus] || 'Checking'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
            <div className="bg-[#1a1508] border border-green-500/15 rounded-lg p-4">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-green-300">OK</p>
              <p className="text-2xl font-bold text-green-300 mt-1">{readinessSummary.ok || 0}</p>
            </div>
            <div className="bg-[#1a1508] border border-[#cba135]/15 rounded-lg p-4">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Warnings</p>
              <p className="text-2xl font-bold text-[#cba135] mt-1">{readinessSummary.warning || 0}</p>
            </div>
            <div className="bg-[#1a1508] border border-red-500/15 rounded-lg p-4">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-red-300">Actions</p>
              <p className="text-2xl font-bold text-red-300 mt-1">{readinessSummary.actionRequired || 0}</p>
            </div>
            <div className="bg-[#1a1508] border border-red-500/15 rounded-lg p-4">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-red-300">Blockers</p>
              <p className="text-2xl font-bold text-red-300 mt-1">{readinessSummary.blockers || 0}</p>
            </div>
          </div>

          {!readiness ? (
            <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6">
              <p className="text-sm text-[#cba135]/70">Readiness check is loading.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {readinessItems.map((item) => (
                <div
                  key={item.key}
                  className={`border rounded-lg p-4 ${readinessItemClass(item.status)}`}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-[180px_1fr_auto] gap-3 lg:gap-6">
                    <div className="flex items-center gap-3">
                      <span className={`h-2.5 w-2.5 rounded-full ${readinessDotClass(item.status)}`} />
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">{item.category}</p>
                        <p className="text-sm font-semibold text-[#cba135]">{item.label}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-[#cba135]/80">{item.detail}</p>
                      {item.action && (
                        <p className="text-sm text-[#cba135] mt-1">
                          <WarningCircle size={15} weight="fill" className="inline mr-1 align-[-2px]" />
                          {item.action}
                        </p>
                      )}
                    </div>
                    <span className={`inline-flex w-fit items-center rounded-md px-3 py-2 text-xs uppercase tracking-[0.15em] font-bold ${
                      item.status === 'ok'
                        ? 'bg-green-500/10 text-green-300'
                        : item.status === 'warning'
                          ? 'bg-[#cba135]/10 text-[#cba135]'
                          : 'bg-red-500/10 text-red-300'
                    }`}>
                      {item.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <ShieldCheck size={30} weight="duotone" className="text-[#cba135]" />
              <h3 className="heading-font text-xl sm:text-2xl font-medium text-[#cba135]">Controlled Autopilot</h3>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <Button
                data-testid="run-autopilot-button"
                onClick={handleRunAutopilot}
                disabled={loading || autopilot?.running}
                className="bg-[#cba135] text-[#0e0b04] hover:bg-[#a8872b] font-bold"
              >
                <ShieldCheck size={18} className="mr-2" />
                {autopilot?.running ? 'Running' : 'Run Autopilot'}
              </Button>
              <span className={`inline-flex w-fit items-center rounded-md border px-3 py-2 text-xs uppercase tracking-[0.15em] font-bold ${
                autopilot?.enabled
                  ? 'bg-green-500/10 text-green-300 border-green-500/20'
                  : 'bg-[#cba135]/10 text-[#cba135] border-[#cba135]/20'
              }`}>
                {autopilot?.enabled ? `Every ${autopilot.intervalSeconds || 120}s` : 'Disabled'}
              </span>
            </div>
          </div>

          {!autopilot ? (
            <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6">
              <p className="text-sm text-[#cba135]/70">Autopilot status is loading.</p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">KYC Ready</p>
                  <p className="text-2xl font-bold text-[#cba135] mt-1">{autopilotQueues.kycReady || 0}</p>
                  <p className="text-xs text-[#cba135]/55 mt-1">{autopilotQueues.kycUnnotified || 0} unnotified</p>
                </div>
                <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Paid Tickets</p>
                  <p className="text-2xl font-bold text-[#cba135] mt-1">{autopilotQueues.paidPaymentsWithoutTicket || 0}</p>
                  <p className="text-xs text-[#cba135]/55 mt-1">waiting issue</p>
                </div>
                <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Full Draws</p>
                  <p className="text-2xl font-bold text-[#cba135] mt-1">{autopilotQueues.fullDrawsWaiting || 0}</p>
                  <p className="text-xs text-[#cba135]/55 mt-1">waiting draw</p>
                </div>
                <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Withdrawals</p>
                  <p className="text-2xl font-bold text-[#cba135] mt-1">{autopilotQueues.withdrawalsUnscored || 0}</p>
                  <p className="text-xs text-[#cba135]/55 mt-1">risk unchecked</p>
                </div>
                <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Provider Sync</p>
                  <p className="text-2xl font-bold text-[#cba135] mt-1">{autopilotQueues.providerPaymentsWaitingSync || 0}</p>
                  <p className="text-xs text-[#cba135]/55 mt-1">{autopilot.providerSyncEnabled ? 'enabled' : 'manual/webhook'}</p>
                </div>
              </div>

              <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-5">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Last run</p>
                    <p className="text-sm text-[#cba135] mt-1">
                      {autopilotLastRun
                        ? `${autopilotLastRun.status} / ${new Date(autopilotLastRun.finishedAt || autopilotLastRun.startedAt).toLocaleString()}`
                        : 'No run recorded yet'}
                    </p>
                    <p className="text-xs text-[#cba135]/55 mt-1">
                      Guardrails: no automatic KYC approval, no automatic payout, tickets only after paid/confirmed payment.
                    </p>
                  </div>
                  {autopilotLastRun?.summary && (
                    <div className="grid grid-cols-3 gap-3 min-w-[260px]">
                      <div className="border border-[#cba135]/10 rounded-md p-3">
                        <p className="text-xs uppercase tracking-[0.15em] text-[#a8872b]">Actions</p>
                        <p className="text-lg font-bold text-[#cba135]">{autopilotLastRun.summary.actions || 0}</p>
                      </div>
                      <div className="border border-[#cba135]/10 rounded-md p-3">
                        <p className="text-xs uppercase tracking-[0.15em] text-[#a8872b]">Warnings</p>
                        <p className="text-lg font-bold text-[#cba135]">{autopilotLastRun.summary.warnings || 0}</p>
                      </div>
                      <div className="border border-red-500/20 rounded-md p-3">
                        <p className="text-xs uppercase tracking-[0.15em] text-red-300">Errors</p>
                        <p className="text-lg font-bold text-red-300">{autopilotLastRun.summary.errors || 0}</p>
                      </div>
                    </div>
                  )}
                </div>
                {autopilotLastActions.length > 0 && (
                  <div className="mt-4 divide-y divide-[#cba135]/10">
                    {autopilotLastActions.slice(0, 6).map((action, index) => (
                      <div key={`${action.task}-${index}`} className="py-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-[#cba135]">{action.task}</p>
                          <p className="text-xs text-[#cba135]/65">{action.detail}</p>
                        </div>
                        <span className={`inline-flex w-fit px-3 py-1 rounded-md text-xs uppercase tracking-[0.15em] font-bold ${
                          action.status === 'ok'
                            ? 'bg-green-500/10 text-green-300'
                            : action.status === 'warning'
                              ? 'bg-[#cba135]/10 text-[#cba135]'
                              : 'bg-red-500/10 text-red-300'
                        }`}>
                          {action.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="mb-12"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <ChartBar size={30} weight="duotone" className="text-[#cba135]" />
              <h3 className="heading-font text-xl sm:text-2xl font-medium text-[#cba135]">Payment Reconciliation</h3>
            </div>
            <span
              data-testid="reconciliation-status"
              className={`inline-flex w-fit items-center rounded-md border px-3 py-2 text-xs uppercase tracking-[0.15em] font-bold ${reconciliationStatusClass[reconciliation?.overallStatus] || reconciliationStatusClass.needs_attention}`}
            >
              {reconciliation?.overallStatus === 'ok' ? 'Balanced' : 'Needs review'}
            </span>
          </div>

          {!reconciliation ? (
            <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6">
              <p className="text-sm text-[#cba135]/70">Reconciliation is loading.</p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Tickets</p>
                  <p className="text-2xl font-bold text-[#cba135] mt-1">{reconciliationSummary.issuedTickets || 0}</p>
                  <p className="text-xs text-[#cba135]/55 mt-1">{reconciliationSummary.paidTicketPayments || 0} paid payments</p>
                </div>
                <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Gross</p>
                  <p className="text-2xl font-bold text-[#cba135] mt-1">{formatEur(reconciliationSummary.ledgerGross)}</p>
                  <p className="text-xs text-[#cba135]/55 mt-1">expected {formatEur(reconciliationSummary.grossExpected)}</p>
                </div>
                <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Pool</p>
                  <p className="text-2xl font-bold text-[#cba135] mt-1">{formatEur(reconciliationSummary.ledgerPool)}</p>
                  <p className="text-xs text-[#cba135]/55 mt-1">expected {formatEur(reconciliationSummary.poolExpected)}</p>
                </div>
                <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Fee</p>
                  <p className="text-2xl font-bold text-[#cba135] mt-1">{formatEur(reconciliationSummary.ledgerPlatformFee)}</p>
                  <p className="text-xs text-[#cba135]/55 mt-1">expected {formatEur(reconciliationSummary.platformFeeExpected)}</p>
                </div>
              </div>

              {reconciliationIssues.length === 0 ? (
                <div className="bg-green-500/5 border border-green-500/15 rounded-lg p-5">
                  <p className="text-sm text-green-300">Payments, tickets, draw counters and ledger allocations are balanced.</p>
                </div>
              ) : (
                <div className="bg-[#1a1508] border border-red-500/20 rounded-lg overflow-hidden">
                  <div className="px-5 py-4 border-b border-red-500/15">
                    <p className="text-sm font-semibold text-red-300">{reconciliationIssues.length} reconciliation issue{reconciliationIssues.length === 1 ? '' : 's'} need review</p>
                  </div>
                  <div className="divide-y divide-red-500/10">
                    {reconciliationIssues.slice(0, 8).map((issue, index) => (
                      <div key={`${issue.code}-${index}`} className="p-5">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div>
                            <p className="text-xs uppercase tracking-[0.2em] font-bold text-red-300">{issue.category} / {issue.code}</p>
                            <p className="text-sm text-[#cba135] mt-1">{issue.detail}</p>
                            {issue.action && <p className="text-xs text-[#cba135]/60 mt-1">{issue.action}</p>}
                          </div>
                          <span className="inline-flex w-fit px-3 py-1 rounded-md text-xs uppercase tracking-[0.15em] font-bold bg-red-500/10 text-red-300">
                            {issue.severity}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="mb-12"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <h3 className="heading-font text-xl sm:text-2xl font-medium text-[#cba135]">Payments</h3>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">
              {payments.filter((payment) => paymentReviewStatuses.includes(payment.status)).length} needs review
            </span>
          </div>

          {payments.length === 0 ? (
            <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6">
              <p className="text-sm text-[#cba135]/70">No payment intents yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {payments.slice(0, 12).map((payment) => (
                <div
                  key={payment.id}
                  className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr_auto] gap-6">
                    <div className="flex items-start gap-4">
                      <CreditCard size={32} weight="duotone" className="text-[#cba135] mt-1" />
                      <div>
                        <h4 className="heading-font text-lg font-medium text-[#cba135] mb-2">
                          {payment.userEmail || payment.userId}
                        </h4>
                        <div className="space-y-1 text-sm text-[#cba135]/75">
                          <p>Type: <span className="text-[#cba135]">{payment.type || 'wallet_deposit'}</span></p>
                          <p>Provider: <span className="text-[#cba135]">{payment.provider}</span></p>
                          <p>Environment: <span className="text-[#cba135]">{payment.providerEnvironment || '-'}</span></p>
                          <p>Status: <span className="text-[#cba135]">{payment.status}</span></p>
                          <p>Provider status: <span className="text-[#cba135]">{payment.providerStatus || '-'}</span></p>
                          {payment.drawNumber && <p>Round: <span className="text-[#cba135]">#{payment.drawNumber}</span></p>}
                          {payment.ticketNumber && <p>Ticket: <span className="text-[#cba135]">#{payment.ticketNumber}</span></p>}
                          <p>Callbacks: <span className="text-[#cba135]">{payment.callbackCount || 0}</span></p>
                          <p>Created: <span className="text-[#cba135]">{new Date(payment.createdAt).toLocaleString()}</span></p>
                          {payment.providerTxHash && (
                            <p>Tx: <span className="text-[#cba135] break-all">{payment.providerTxHash}</span></p>
                          )}
                          {payment.ticketIssueError && (
                            <p>Error: <span className="text-red-300">{payment.ticketIssueError}</span></p>
                          )}
                          {payment.manualReviewReason && (
                            <p>Review: <span className="text-red-300">{payment.manualReviewReason}</span></p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="lg:text-right">
                      <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b] mb-1">Amount</p>
                      <p className="heading-font text-2xl font-semibold text-[#cba135]">
                        {payment.currency || 'EUR'} {Number(payment.expectedAmount || payment.amount || 0).toFixed(2)}
                      </p>
                      {(payment.paidAmount || payment.overpaidAmount || payment.underpaidAmount) && (
                        <div className="mt-2 text-xs text-[#cba135]/60 space-y-1">
                          <p>Paid: {payment.paidCurrency || payment.providerCurrency || '-'} {Number(payment.paidAmount || 0).toFixed(2)}</p>
                          {payment.underpaidAmount > 0 && <p className="text-red-300">Short: EUR {Number(payment.underpaidAmount).toFixed(2)}</p>}
                          {payment.overpaidAmount > 0 && <p className="text-green-300">Extra: EUR {Number(payment.overpaidAmount).toFixed(2)}</p>}
                        </div>
                      )}
                      {payment.acceptedMin && payment.acceptedMax && (
                        <p className="text-xs text-[#cba135]/50 mt-2">
                          Accepted EUR {Number(payment.acceptedMin).toFixed(2)}-{Number(payment.acceptedMax).toFixed(2)}
                        </p>
                      )}
                      {(payment.providerCurrency || payment.providerNetwork) && (
                        <p className="text-xs text-[#cba135]/70 mt-2" data-testid={`payment-network-${payment.id}`}>
                          <span className="text-[#cba135]/50">Pref:</span>{' '}
                          <span className="font-semibold text-[#cba135]">
                            {payment.providerCurrency || '-'}
                          </span>
                          <span className="text-[#cba135]/50"> on </span>
                          <span className="font-semibold text-[#cba135]">
                            {payment.providerNetwork || '-'}
                          </span>
                          {payment.paidNetwork && payment.paidNetwork !== payment.providerNetwork && (
                            <span className="ml-1 text-amber-300">
                              · paid: {payment.paidCurrency || payment.providerCurrency} on {payment.paidNetwork}
                            </span>
                          )}
                        </p>
                      )}
                      <p className="text-xs text-[#cba135]/50 mt-2 break-all">
                        {payment.providerPaymentId}
                      </p>
                      {payment.providerTxHash && (
                        <p
                          className="text-[10px] text-[#cba135]/50 mt-1 break-all font-mono"
                          title={payment.providerTxHash}
                          data-testid={`payment-tx-hash-${payment.id}`}
                        >
                          tx: {payment.providerTxHash.slice(0, 12)}…{payment.providerTxHash.slice(-8)}
                        </p>
                      )}
                    </div>

                    <div className="lg:min-w-[150px] space-y-3">
                      <span className={`inline-flex px-3 py-2 rounded-md text-xs uppercase tracking-[0.15em] font-bold ${paymentStatusClass(payment.status)}`}>
                        {payment.status}
                      </span>
                      {canSyncPayment(payment) && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={loading}
                          onClick={() => handleSyncPayment(payment.id)}
                          className="w-full border-[#cba135]/20 text-[#cba135] hover:bg-[#cba135]/10"
                        >
                          Sync
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-12"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <h3 className="heading-font text-xl sm:text-2xl font-medium text-[#cba135]">Risk Flags</h3>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">
              {riskFlags.filter((flag) => flag.status === 'open').length} open
            </span>
          </div>

          {riskFlags.length === 0 ? (
            <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6">
              <p className="text-sm text-[#cba135]/70">No risk flags yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {riskFlags.slice(0, 10).map((flag) => (
                <div key={flag.id} className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr_auto] gap-6">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`h-2.5 w-2.5 rounded-full ${flag.severity === 'high' ? 'bg-red-400' : 'bg-[#cba135]'}`} />
                        <h4 className="heading-font text-lg font-medium text-[#cba135]">{flag.category}</h4>
                      </div>
                      <div className="space-y-1 text-sm text-[#cba135]/75">
                        <p>User: <span className="text-[#cba135]">{flag.userEmail || flag.userId}</span></p>
                        <p>Reason: <span className="text-[#cba135]">{flag.reason}</span></p>
                        <p>Status: <span className="text-[#cba135]">{flag.status}</span></p>
                        <p>Created: <span className="text-[#cba135]">{new Date(flag.createdAt).toLocaleString()}</span></p>
                      </div>
                    </div>
                    <div className="lg:text-right">
                      <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b] mb-1">Amount</p>
                      <p className="heading-font text-2xl font-semibold text-[#cba135]">EUR {Number(flag.amount || 0).toFixed(2)}</p>
                      <p className="text-sm text-[#cba135]/60 mt-1">{flag.severity}</p>
                    </div>
                    <div className="flex lg:flex-col gap-3 lg:min-w-[150px]">
                      <Button
                        data-testid={`resolve-risk-${flag.id}`}
                        onClick={() => handleResolveRiskFlag(flag.id)}
                        disabled={loading || flag.status === 'resolved'}
                        className="bg-[#cba135] text-[#0e0b04] hover:bg-[#a8872b] font-bold flex-1 lg:flex-none"
                      >
                        <CheckCircle size={18} className="mr-2" />
                        Resolve
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-12"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <h3 className="heading-font text-xl sm:text-2xl font-medium text-[#cba135]">Withdrawal Queue</h3>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">
              {withdrawals.filter((withdrawal) => withdrawal.status === 'pending').length} pending
            </span>
          </div>

          {withdrawals.length === 0 ? (
            <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6">
              <p className="text-sm text-[#cba135]/70">No withdrawal requests yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {withdrawals.map((withdrawal) => (
                <div
                  key={withdrawal.id}
                  className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_auto] gap-6">
                    <div className="flex items-start gap-4">
                      <Bank size={32} weight="duotone" className="text-[#cba135] mt-1" />
                      <div>
                        <h4 className="heading-font text-lg font-medium text-[#cba135] mb-2">
                          {withdrawal.userName || withdrawal.userEmail}
                        </h4>
                        <div className="space-y-1 text-sm text-[#cba135]/75">
                          <p>Email: <span className="text-[#cba135]">{withdrawal.userEmail}</span></p>
                          <p>IBAN: <span className="text-[#cba135] break-all">{withdrawal.bankAccountIban}</span></p>
                          <p>Requested: <span className="text-[#cba135]">{new Date(withdrawal.requestedAt).toLocaleString()}</span></p>
                          <p>Status: <span className="text-[#cba135]">{withdrawal.status}</span></p>
                          {withdrawal.riskStatus && (
                            <p>Risk: <span className={withdrawal.riskSeverity === 'high' ? 'text-red-300' : 'text-[#cba135]'}>
                              {withdrawal.riskStatus} / {withdrawal.riskSeverity}
                            </span></p>
                          )}
                          {withdrawal.reviewReason && (
                            <p>Reason: <span className="text-[#cba135]">{withdrawal.reviewReason}</span></p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="lg:text-right">
                      <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b] mb-1">Amount</p>
                      <p className="heading-font text-3xl font-semibold text-[#cba135]">
                        EUR {Number(withdrawal.amount || 0).toFixed(2)}
                      </p>
                    </div>

                    <div className="flex lg:flex-col gap-3 lg:min-w-[150px]">
                      <Button
                        data-testid={`approve-withdrawal-${withdrawal.id}`}
                        onClick={() => handleWithdrawalReview(withdrawal.id, 'approve')}
                        disabled={loading || withdrawal.status !== 'pending'}
                        className="bg-[#cba135] text-[#0e0b04] hover:bg-[#a8872b] font-bold flex-1 lg:flex-none"
                      >
                        <CheckCircle size={18} className="mr-2" />
                        Paid
                      </Button>
                      <Button
                        data-testid={`reject-withdrawal-${withdrawal.id}`}
                        onClick={() => handleRejectWithdrawal(withdrawal.id)}
                        disabled={loading || withdrawal.status !== 'pending'}
                        variant="outline"
                        className="border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200 flex-1 lg:flex-none"
                      >
                        <XCircle size={18} className="mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <div className="flex justify-between items-center mb-6">
            <h3 className="heading-font text-xl sm:text-2xl font-medium text-[#cba135]">Draws Management</h3>
            <Button
              data-testid="create-draw-button"
              onClick={() => setShowCreateDraw(true)}
              className="bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-bold"
            >
              <Plus size={20} className="mr-2" />
              {t.createDraw}
            </Button>
          </div>

          <div className="space-y-4">
            {draws.map((draw) => (
              <div
                key={draw.id}
                className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="heading-font text-lg font-medium text-[#cba135] mb-1">
                      Draw #{draw.drawNumber}
                    </h4>
                    <p className="text-sm text-[#cba135]/70">
                      Status: {draw.status === 'active' ? 
                        <span className="text-green-500">Active</span> : 
                        <span className="text-[#a8872b]">Completed</span>
                      }
                    </p>
                    <p className="text-sm text-[#cba135]/70">
                      Tickets: {draw.ticketsSold} / {draw.maxTickets}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b] mb-1">Platform Fee</p>
                    <p className="text-lg font-semibold text-[#cba135]">
                      EUR {Number(draw.platformFees || 0).toFixed(2)}
                    </p>
                    <p className="text-xs text-[#cba135]/50">Pool EUR {Number(draw.prizePool || 0).toFixed(2)}</p>
                  </div>
                </div>
                {draw.status === 'completed' && draw.winners && (
                  <div className="mt-4 pt-4 border-t border-[#cba135]/10">
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b] mb-2">Winners</p>
                    <p className="text-sm text-[#cba135]">
                      1st Prize: Ticket #{draw.winners.firstPrize.ticketNumber} - EUR {Number(draw.winners.firstPrize.amount || 0).toLocaleString()}
                    </p>
                    <p className="text-sm text-[#cba135]">
                      2nd Prize: {draw.winners.secondPrize?.length || 0} winners - EUR {Number(draw.winners.secondPrize?.[0]?.amount || 0).toLocaleString()} each
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <h3 className="heading-font text-xl sm:text-2xl font-medium text-[#cba135]">KYC Review Queue</h3>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">
              {kycCases.length} pending case{kycCases.length === 1 ? '' : 's'}
            </span>
          </div>

          {kycCases.length === 0 ? (
            <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6">
              <p className="text-sm text-[#cba135]/70">No KYC cases are waiting for review.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {kycCases.map((kycCase) => (
                <div
                  key={kycCase.id}
                  className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr_auto] gap-6">
                    <div>
                      <h4 className="heading-font text-lg font-medium text-[#cba135] mb-2">
                        {kycCase.name || kycCase.username || kycCase.email}
                      </h4>
                      <div className="space-y-1 text-sm text-[#cba135]/75">
                        <p>Email: <span className="text-[#cba135]">{kycCase.email}</span></p>
                        <p>Nick: <span className="text-[#cba135]">{kycCase.username || '-'}</span></p>
                        <p>IBAN: <span className="text-[#cba135]">{kycCase.bankAccountIban || '-'}</span></p>
                        <p>Email verified: <span className="text-[#cba135]">{kycCase.emailVerified ? 'Yes' : 'No'}</span></p>
                        <p>Status: <span className="text-[#cba135]">{kycCase.verificationStatus || 'pending'}</span></p>
                        <p>KYC review: <span className="text-[#cba135]">{kycCase.kycReviewStatus || 'not_started'}</span></p>
                        {kycCase.kycReviewReason && (
                          <p>Reason: <span className="text-[#cba135]">{kycCase.kycReviewReason}</span></p>
                        )}
                      </div>
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b] mb-3">Documents</p>
                      <div className="space-y-2">
                        {Object.entries(documentLabels).map(([type, label]) => {
                          const document = kycCase.documents?.[type];
                          return (
                            <div key={type} className="flex items-center justify-between gap-3 border border-[#cba135]/10 rounded-md px-3 py-2">
                              <div className="min-w-0">
                                <p className="text-sm text-[#cba135]">{label}</p>
                                <p className="text-xs text-[#cba135]/55 truncate">
                                  {document?.originalFilename || 'Missing'}
                                </p>
                              </div>
                              <Button
                                data-testid={`view-kyc-${type}-${kycCase.id}`}
                                size="sm"
                                variant="outline"
                                onClick={() => openKycDocument(kycCase.id, type)}
                                disabled={!document}
                                className="border-[#cba135]/20 text-[#cba135] hover:bg-[#cba135]/10 hover:text-[#cba135]"
                              >
                                <Eye size={16} className="mr-2" />
                                View
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="flex lg:flex-col gap-3 lg:min-w-[150px]">
                      <Button
                        data-testid={`approve-kyc-case-${kycCase.id}`}
                        onClick={() => handleReviewKyc(kycCase.id, 'approved')}
                        disabled={loading}
                        className="bg-[#cba135] text-[#0e0b04] hover:bg-[#a8872b] font-bold flex-1 lg:flex-none"
                      >
                        <CheckCircle size={18} className="mr-2" />
                        Approve
                      </Button>
                      <Button
                        data-testid={`reject-kyc-case-${kycCase.id}`}
                        onClick={() => handleRejectKyc(kycCase.id)}
                        disabled={loading}
                        variant="outline"
                        className="border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200 flex-1 lg:flex-none"
                      >
                        <XCircle size={18} className="mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-12"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <h3 className="heading-font text-xl sm:text-2xl font-medium text-[#cba135]">Audit Log</h3>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">
              Last {Math.min(auditLogs.length, 10)}
            </span>
          </div>

          {auditLogs.length === 0 ? (
            <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-6">
              <p className="text-sm text-[#cba135]/70">No admin audit events yet.</p>
            </div>
          ) : (
            <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className="border-b border-[#cba135]/10">
                      <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Action</th>
                      <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Actor</th>
                      <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Target</th>
                      <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.slice(0, 10).map((log) => (
                      <tr key={log.id} className="border-b border-[#cba135]/10 hover:bg-[#cba135]/5">
                        <td className="p-4 text-[#cba135]">{log.action}</td>
                        <td className="p-4 text-[#cba135]">{log.actorEmail || log.actorId}</td>
                        <td className="p-4 text-[#cba135]">{log.targetType}:{log.targetId}</td>
                        <td className="p-4 text-[#cba135]">{new Date(log.createdAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
            <h3 className="heading-font text-xl sm:text-2xl font-medium text-[#cba135]">
              Users <span className="text-sm text-[#cba135]/50 ml-2">({usersData.total} total)</span>
            </h3>
            <div className="flex items-center gap-3">
              {usersLoading && <span className="text-xs text-[#cba135]/50">Loading…</span>}
              <TestAccountsCleanupPanel token={token} onMutated={fetchUsers} />
            </div>
          </div>

          <div className="bg-[#1a1508] border border-[#cba135]/15 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="lg:col-span-2">
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-[#a8872b] block mb-1">Search</label>
                <Input
                  data-testid="users-search-input"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Email, name, username, phone, IBAN prefix…"
                  className="bg-[#0e0b04] border-[#cba135]/20 text-[#cba135]"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-[#a8872b] block mb-1">Account status</label>
                <select
                  data-testid="users-filter-account-status"
                  value={filters.accountStatus}
                  onChange={(e) => updateFilter('accountStatus', e.target.value)}
                  className="w-full bg-[#0e0b04] border border-[#cba135]/20 text-[#cba135] rounded h-10 px-3 text-sm"
                >
                  <option value="">Any</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="anonymized">Anonymized</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-[#a8872b] block mb-1">Role</label>
                <select
                  data-testid="users-filter-role"
                  value={filters.role}
                  onChange={(e) => updateFilter('role', e.target.value)}
                  className="w-full bg-[#0e0b04] border border-[#cba135]/20 text-[#cba135] rounded h-10 px-3 text-sm"
                >
                  <option value="">Any</option>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-[#a8872b] block mb-1">Verification</label>
                <select
                  data-testid="users-filter-verification"
                  value={filters.verificationStatus}
                  onChange={(e) => updateFilter('verificationStatus', e.target.value)}
                  className="w-full bg-[#0e0b04] border border-[#cba135]/20 text-[#cba135] rounded h-10 px-3 text-sm"
                >
                  <option value="">Any</option>
                  <option value="active">Active</option>
                  <option value="verified">Verified</option>
                  <option value="pending_identity">Pending Identity</option>
                  <option value="pending_review">Pending Review</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-[#a8872b] block mb-1">Country (ISO)</label>
                <Input
                  data-testid="users-filter-country"
                  value={filters.country}
                  onChange={(e) => updateFilter('country', e.target.value.toUpperCase())}
                  placeholder="SK"
                  maxLength={3}
                  className="bg-[#0e0b04] border-[#cba135]/20 text-[#cba135] uppercase"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-[#a8872b] block mb-1">Registered ≤ days</label>
                <Input
                  data-testid="users-filter-days"
                  type="number"
                  min="1"
                  value={filters.registeredWithinDays}
                  onChange={(e) => updateFilter('registeredWithinDays', e.target.value)}
                  placeholder="7, 30, 90…"
                  className="bg-[#0e0b04] border-[#cba135]/20 text-[#cba135]"
                />
              </div>
              <div className="flex items-end">
                <Button
                  data-testid="users-clear-filters"
                  variant="outline"
                  onClick={clearFilters}
                  className="border-[#cba135]/30 text-[#cba135]/80 hover:bg-[#cba135]/10 w-full"
                >
                  Clear all
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="border-b border-[#cba135]/10">
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Name</th>
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Email</th>
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Balance</th>
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">KYC</th>
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Role</th>
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-[#cba135]/10 hover:bg-[#cba135]/5">
                      <td className="p-4 text-[#cba135]">{u.name}</td>
                      <td className="p-4 text-[#cba135]">{u.email}</td>
                      <td className="p-4 text-[#cba135]">€{u.walletBalance.toFixed(2)}</td>
                      <td className="p-4 text-[#cba135]">{u.verificationStatus || 'pending_kyc'}</td>
                      <td className="p-4 text-[#cba135]">{u.role}</td>
                      <td className="p-4 text-[#cba135]">
                        <div className="flex flex-wrap gap-2">
                          <Button
                            data-testid={`view-user-${u.id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => setDetailUserId(u.id)}
                            className="border-[#cba135]/40 text-[#cba135] hover:bg-[#cba135]/10"
                          >
                            <Eye size={14} weight="bold" className="mr-1" />
                            Details
                          </Button>
                          {u.role !== 'admin' && !['active', 'verified'].includes(u.verificationStatus) && (
                            <Button
                              data-testid={`approve-kyc-${u.id}`}
                              size="sm"
                              onClick={() => handleReviewKyc(u.id, 'approved')}
                              disabled={loading}
                              className="bg-[#cba135] text-[#0e0b04] hover:bg-[#a8872b] font-bold"
                            >
                              Approve KYC
                            </Button>
                          )}
                          {u.role !== 'admin' && u.accountStatus !== 'anonymized' && (
                            <Button
                              data-testid={`transfer-admin-${u.id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => handleTransferAdmin(u.id, u.email)}
                              disabled={loading}
                              className="border-orange-500/30 text-orange-300 hover:bg-orange-500/10 hover:text-orange-200"
                              title="Hand over admin role to this user (you will become a regular user)"
                            >
                              Transfer admin role
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {usersData.total > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-t border-[#cba135]/10 bg-[#0e0b04]/50">
                <div className="text-xs text-[#cba135]/70">
                  Showing <span className="text-[#cba135] font-semibold">{(filters.page - 1) * filters.pageSize + 1}</span>
                  {' '}–{' '}
                  <span className="text-[#cba135] font-semibold">
                    {Math.min(filters.page * filters.pageSize, usersData.total)}
                  </span>
                  {' '}of <span className="text-[#cba135] font-semibold">{usersData.total}</span>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    data-testid="users-page-size"
                    value={filters.pageSize}
                    onChange={(e) => updateFilter('pageSize', e.target.value)}
                    className="bg-[#1a1508] border border-[#cba135]/20 text-[#cba135] rounded h-8 px-2 text-xs"
                  >
                    {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
                  </select>
                  <Button
                    data-testid="users-prev-page"
                    size="sm"
                    variant="outline"
                    disabled={filters.page <= 1 || usersLoading}
                    onClick={() => updateFilter('page', filters.page - 1)}
                    className="border-[#cba135]/30 text-[#cba135] hover:bg-[#cba135]/10 h-8"
                  >
                    ← Prev
                  </Button>
                  <span className="text-xs text-[#cba135]/70 px-2">
                    Page {filters.page} / {usersData.totalPages || 1}
                  </span>
                  <Button
                    data-testid="users-next-page"
                    size="sm"
                    variant="outline"
                    disabled={filters.page >= usersData.totalPages || usersLoading}
                    onClick={() => updateFilter('page', filters.page + 1)}
                    className="border-[#cba135]/30 text-[#cba135] hover:bg-[#cba135]/10 h-8"
                  >
                    Next →
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Account Security */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className="bg-gradient-to-br from-[#1a1508]/80 to-[#0e0b04]/80 rounded-2xl border border-[#cba135]/10 p-6">
          <h3 className="heading-font text-lg text-[#cba135] mb-4 flex items-center gap-2">
            <ShieldCheck size={20} /> {t.accountSecurity}
          </h3>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div>
              <Label className="text-[#cba135]/70 text-sm">{t.currentPassword}</Label>
              <PasswordInput
                data-testid="current-password-input"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] focus:ring-[#cba135]"
                required
              />
            </div>
            <div>
              <Label className="text-[#cba135]/70 text-sm">{t.newPassword}</Label>
              <PasswordInput
                data-testid="new-password-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] focus:ring-[#cba135]"
                required
                minLength={12}
              />
            </div>
            <div>
              <Label className="text-[#cba135]/70 text-sm">{t.confirmNewPassword}</Label>
              <PasswordInput
                data-testid="confirm-password-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] focus:ring-[#cba135]"
                required
                minLength={12}
              />
            </div>
            <Button
              data-testid="change-password-button"
              type="submit"
              disabled={passwordLoading}
              className="bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-bold"
            >
              {passwordLoading ? t.changingPassword : t.changePassword}
            </Button>
          </form>
        </motion.div>
      </main>

      {detailUserId && (
        <UserDetailModal
          userId={detailUserId}
          token={token}
          currentAdminId={user?.id}
          onClose={() => setDetailUserId(null)}
          onMutated={fetchData}
        />
      )}

      <Dialog open={showCreateDraw} onOpenChange={setShowCreateDraw}>
        <DialogContent className="bg-[#0e0b04] border border-[#cba135]/20 backdrop-blur-2xl">
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl text-[#cba135]">Create New Draw</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateDraw} className="space-y-4">
            <div>
              <Label htmlFor="drawNumber" className="text-[#cba135]">Draw Number</Label>
              <Input
                id="drawNumber"
                data-testid="draw-number-input"
                type="number"
                min="1"
                value={drawNumber}
                onChange={(e) => setDrawNumber(e.target.value)}
                className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] focus:ring-[#cba135]"
                required
              />
            </div>
            <div className="bg-[#1a1508] border border-[#cba135]/10 rounded-lg p-4 space-y-2">
              <p className="text-sm text-[#cba135]">Max Tickets: <span className="font-bold">2000</span></p>
              <p className="text-sm text-[#cba135]">Ticket Price: <span className="font-bold">EUR 150</span></p>
              <p className="text-sm text-[#cba135]">Pool Split: <span className="font-bold">EUR 100 pool / EUR 50 fee</span></p>
              <p className="text-sm text-[#cba135]">Prizes: <span className="font-bold">configured by backend round rules</span></p>
            </div>
            <Button 
              data-testid="confirm-create-draw-button"
              type="submit" 
              className="w-full bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-bold"
              disabled={loading}
            >
              {loading ? '...' : t.createDraw}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;

// ─── R2 Offsite Backup Status (lives inside admin Backups card) ────────────
const R2BackupStatus = () => {
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem('lottery_token');
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/backups/r2/status`, { headers: authHeaders });
      if (!r.ok) {
        const txt = await r.text().catch(() => '');
        setLoadError(`HTTP ${r.status}${txt ? ` · ${txt.slice(0, 120)}` : ''}`);
        setStatus(null);
        return;
      }
      const d = await r.json();
      setStatus(d);
      setLoadError(null);
    } catch (err) {
      console.warn('R2 status load failed', err);
      setLoadError(err?.message || 'Network error');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/backups/r2/run`, { method: 'POST', headers: authHeaders });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${r.status}`);
      }
      const d = await r.json();
      toast.success(`Backup uploaded to R2: ${d.key}`);
      refresh();
    } catch (err) {
      toast.error(err.message || 'R2 upload failed');
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return (
      <div className="mt-5 pt-5 border-t border-[#cba135]/15" data-testid="r2-backup-panel">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b] mb-1">
          Offsite — Cloudflare R2
        </p>
        {loading ? (
          <p className="text-sm text-[#cba135]/60">Loading R2 status…</p>
        ) : (
          <p className="text-sm text-red-300/85" data-testid="r2-status-error">
            Could not load R2 status{loadError ? ` (${loadError})` : ''}.
            Verify you are logged in as admin and that the backend is reachable.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="mt-5 pt-5 border-t border-[#cba135]/15" data-testid="r2-backup-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b] mb-1">
            Offsite — Cloudflare R2
          </p>
          {status.configured ? (
            <p className="text-sm text-[#cba135]/85">
              Bucket <code className="text-[#e6be4b]">{status.bucket}</code> · auto every {status.intervalHours}h · keep {status.retentionDays}d
              {status.lastUpload?.createdAt && (
                <>
                  {' · '}last upload <span className="text-[#e6be4b]">{new Date(status.lastUpload.createdAt).toLocaleString()}</span>
                </>
              )}
            </p>
          ) : (
            <p className="text-sm text-[#cba135]/65">
              Not configured. Set <code className="text-[#e6be4b]">R2_ACCOUNT_ID</code>, <code className="text-[#e6be4b]">R2_ACCESS_KEY_ID</code>, <code className="text-[#e6be4b]">R2_SECRET_ACCESS_KEY</code>, <code className="text-[#e6be4b]">R2_BUCKET</code> in backend env.
            </p>
          )}
        </div>
        {status.configured && (
          <Button
            onClick={runNow}
            disabled={busy}
            data-testid="r2-backup-run-button"
            className="bg-[#cba135]/15 border border-[#cba135]/40 text-[#cba135] hover:bg-[#cba135]/25 font-semibold"
          >
            {busy ? 'Uploading…' : 'Backup now'}
          </Button>
        )}
      </div>
    </div>
  );
};
